/**
 * useFocusTrap — keeps keyboard focus inside a modal surface (dialog,
 * drawer) while it is open, and restores focus to the element that had
 * it when the surface closes.
 *
 * A surface that sets `aria-modal="true"` is promising assistive tech
 * that the rest of the page is inert. Tab must honour that promise:
 * without a trap, Tab walks straight out of the dialog and onto the
 * (visually hidden but still operable) gallery behind it. The Lightbox
 * already trapped its own focus inline; this hook is the same logic,
 * shared so every modal in the flow behaves identically.
 *
 * Returns a ref to attach to the surface's container element. The trap
 * is re-queried on every Tab press, so focusables that mount/unmount
 * while the dialog is open (an error line, a disabled→enabled button)
 * are always accounted for.
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), ' +
  '[tabindex]:not([tabindex="-1"])';

export function useFocusTrap<T extends HTMLElement>(
  active = true,
): React.RefObject<T> {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Remember who was focused so we can hand it back on close — a
    // keyboard user returns to exactly where they left off.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      // This trap is the topmost surface (capture phase, last mounted).
      // Stop the event so a trap on a lower layer — e.g. the Lightbox's
      // own Tab handler when the comments drawer sits over it — doesn't
      // also move focus and fight this one.
      e.stopPropagation();
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(el => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) {
        // Nothing tabbable inside — keep focus pinned to the container
        // rather than letting it escape to the page behind.
        e.preventDefault();
        container.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;
      // Focus outside the trap (or on the container itself) → pull it
      // back to the appropriate edge.
      if (!container.contains(activeEl as Node)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Capture phase so the trap wins even when a modal is layered over
    // another (comments drawer over the lightbox).
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Restore focus only if it's still safe to (the element is still
      // in the document and focusable).
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active]);

  return containerRef;
}
